import React, { useEffect, useState } from 'react';
import { FiShield } from 'react-icons/fi';
import { apiCall, ENDPOINTS } from '../../config/api';
import { useAuth } from '../../contexts/AuthContext';
import Toast from '../../components/Toast';
import { useToast } from '../../hooks/useToast';

const Preferences: React.FC = () => {
  const { token } = useAuth();
  const { toast, showToast, hideToast } = useToast();

  const [mfaEnabled, setMfaEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState(false);
  const [totpCode, setTotpCode] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchMfaStatus();
  }, [token]);

  const fetchMfaStatus = async () => {
    if (!token) return;
    try {
      setLoading(true);
      const response = await apiCall(ENDPOINTS.AUTH.MFA_STATUS, { method: 'GET' }, token);
      if (response.ok) {
        const data = await response.json();
        setMfaEnabled(!!data.mfa_enabled);
      }
    } catch (error) {
      console.error('Error fetching MFA status:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDisableClick = () => {
    setTotpCode('');
    setConfirming(true);
  };

  const handleCancel = () => {
    setConfirming(false);
    setTotpCode('');
  };

  const handleConfirmDisable = async () => {
    if (!token || totpCode.trim().length === 0) return;
    try {
      setSubmitting(true);
      const response = await apiCall(
        ENDPOINTS.AUTH.MFA_DISABLE,
        { method: 'POST', body: JSON.stringify({ totp_code: totpCode.trim() }) },
        token
      );
      const data = await response.json();
      if (response.ok) {
        setMfaEnabled(false);
        setConfirming(false);
        setTotpCode('');
        showToast('Two-factor authentication has been disabled', 'success');
      } else {
        showToast(data.error || 'Failed to disable two-factor authentication', 'error');
      }
    } catch (error) {
      console.error('Error disabling MFA:', error);
      showToast('Failed to disable two-factor authentication', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="p-6 max-w-2xl mx-auto">
      <h1 className="text-2xl font-semibold mb-6">Preferences</h1>

      <div className="bg-base-100 border border-base-300 rounded-xl p-5">
        <div className="flex items-center gap-2 mb-1">
          <FiShield className="text-lg" />
          <h2 className="text-lg font-semibold">Two-Factor Authentication</h2>
        </div>
        <p className="text-sm text-base-content/60 mb-4">
          Two-factor authentication adds an extra verification step at login using an
          authenticator app. Disabling it removes that extra step for this account.
        </p>

        {loading ? (
          <p className="text-sm text-base-content/60">Loading...</p>
        ) : !mfaEnabled ? (
          <p className="text-sm text-base-content/60">
            Two-factor authentication is not currently enabled for this account.
          </p>
        ) : !confirming ? (
          <div className="flex items-center justify-between">
            <span className="text-sm">Two-factor authentication is <span className="font-medium text-success">enabled</span>.</span>
            <button
              onClick={handleDisableClick}
              className="btn btn-error btn-sm"
            >
              Disable 2FA
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-sm">
              Enter the current code from your authenticator app to confirm you want to
              disable two-factor authentication.
            </p>
            <input
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              placeholder="123456"
              value={totpCode}
              onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ''))}
              className="input input-bordered w-40"
            />
            <div className="flex gap-2">
              <button
                onClick={handleConfirmDisable}
                disabled={submitting || totpCode.trim().length === 0}
                className="btn btn-error btn-sm"
              >
                {submitting ? 'Disabling...' : 'Confirm Disable'}
              </button>
              <button onClick={handleCancel} className="btn btn-ghost btn-sm" disabled={submitting}>
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>

      {toast.message && (
        <Toast message={toast.message} type={toast.type} visible={toast.visible} onClose={hideToast} />
      )}
    </div>
  );
};

export default Preferences;
